import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setAuthToken } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onUnauthorized = () => setUser(null);
    window.addEventListener("clawback:unauthorized", onUnauthorized);
    return () => window.removeEventListener("clawback:unauthorized", onUnauthorized);
  }, [refresh]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
    // httpOnly cookie is set by backend; token kept as Bearer fallback for cookie-blocked contexts
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setAuthToken(null);
    try { sessionStorage.removeItem("clawback_paywall_seen"); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const actions = useMemo(() => ({ login, register, logout, refresh }), [login, register, logout, refresh]);
  const value = useMemo(() => ({ user, loading, ...actions }), [user, loading, actions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
